import type { KeybindingOverrideMap } from "~/helpers/keybindingOverrides";
import type { KeyboardAction, KeyboardActionCategory, KeyboardActionId } from "~/keyboard/actionCatalog";
import type { KeyboardScope } from "~/keyboard/scope";

import { effectiveBindingsForAction } from "~/keyboard/actionCatalog";
import { areBindingsEquivalent } from "~/keyboard/bindings";

export type ShortcutSource = "custom" | "default" | "disabled";

export interface ShortcutSettingRow {
	actionId: KeyboardActionId;
	bindings: string[];
	category: KeyboardActionCategory;
	label: string;
	searchAliases?: string[];
	source: ShortcutSource;
}

export interface ReadOnlyShortcutRow {
	bindings: string[];
	id: string;
	label: string;
}

export interface ShortcutCategoryGroup {
	category: KeyboardActionCategory;
	rows: ShortcutSettingRow[];
}

export interface ShortcutBindingConflict {
	actionId: KeyboardActionId;
	binding: string;
	label: string;
}

export const shortcutSourceForAction = (
	actionId: KeyboardActionId,
	overrides: KeybindingOverrideMap
): ShortcutSource => {
	const override = overrides[actionId];
	if (override === undefined) return "default";
	if (override === null) return "disabled";
	return "custom";
};

const toShortcutSettingRow = (action: KeyboardAction, overrides: KeybindingOverrideMap): ShortcutSettingRow => ({
	actionId: action.id,
	bindings: effectiveBindingsForAction(action, overrides),
	category: action.category,
	label: action.label,
	searchAliases: action.searchAliases,
	source: shortcutSourceForAction(action.id, overrides)
});

export const buildShortcutSettingRows = (
	actions: KeyboardAction[],
	overrides: KeybindingOverrideMap
): ShortcutSettingRow[] =>
	actions.filter((action) => action.editable).map((action) => toShortcutSettingRow(action, overrides));

export const buildReadOnlyShortcutRows = (actions: KeyboardAction[]): ReadOnlyShortcutRow[] =>
	actions
		.filter((action) => !action.editable)
		.map((action) => ({ bindings: effectiveBindingsForAction(action), id: action.id, label: action.label }));

export const groupShortcutRowsByCategory = (rows: ShortcutSettingRow[]): ShortcutCategoryGroup[] => {
	const groups: ShortcutCategoryGroup[] = [];
	for (const row of rows) {
		const existing = groups.find((group) => group.category === row.category);
		if (existing) existing.rows.push(row);
		else groups.push({ category: row.category, rows: [row] });
	}
	return groups;
};

const rowMatchesQuery = (row: ShortcutSettingRow, query: string): boolean =>
	row.label.toLowerCase().includes(query) ||
	row.bindings.some((binding) => binding.toLowerCase().includes(query)) ||
	(row.searchAliases ?? []).some((alias) => alias.toLowerCase().includes(query));

export const filterShortcutRows = (rows: ShortcutSettingRow[], search: string): ShortcutSettingRow[] => {
	const query = search.trim().toLowerCase();
	if (!query) return rows;
	return rows.filter((row) => rowMatchesQuery(row, query));
};

const scopesCanCollide = (first: KeyboardScope, second: KeyboardScope): boolean =>
	(first === "modal") === (second === "modal");

export const findBindingConflicts = (
	candidateActionId: KeyboardActionId,
	candidateBinding: string,
	actions: KeyboardAction[],
	overrides: KeybindingOverrideMap
): ShortcutBindingConflict[] => {
	const candidate = actions.find((action) => action.id === candidateActionId);
	if (!candidate) return [];
	const conflicts: ShortcutBindingConflict[] = [];
	for (const action of actions) {
		if (action.id === candidateActionId) continue;
		if (!scopesCanCollide(candidate.scope, action.scope)) continue;
		const collidingBinding = effectiveBindingsForAction(action, overrides).find((binding) =>
			areBindingsEquivalent(binding, candidateBinding)
		);
		if (collidingBinding) conflicts.push({ actionId: action.id, binding: collidingBinding, label: action.label });
	}
	return conflicts;
};
